import React, { useEffect, useState } from 'react';
import { putRegistryRow, registryRowId } from '../../../services/aiControlApi';
import type { AiTabProps } from '../AiControlCenter';
import CredentialRefsPanel from '../settings/CredentialRefsPanel';
import TokenEconomicsPanel, { plainUsd, type TokenEconomicsPatch } from '../settings/TokenEconomicsPanel';
import TenantAiSettingsPanel from '../settings/TenantAiSettingsPanel';
import { RubyLoader } from '../../../components/RubyLoader';

const SettingsTab: React.FC<AiTabProps> = ({
  registry, registryError, reloadRegistry, onHeaderStats, showToast,
}) => {
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    onHeaderStats([
      { label: 'Providers', value: String(registry?.providers.length ?? 0), tone: 'white' },
      { label: 'Credit $', value: String(registry?.config.creditUnitPriceUsd ?? '—'), tone: 'blue' },
      { label: '1 Ruby Token', value: registry ? `$${plainUsd(registry.config.usdPerRubyToken ?? 0.00001)}` : '—', tone: 'white' },
    ]);
  }, [registry, onHeaderStats]);

  const saveConfig = async (patch: TokenEconomicsPatch) => {
    setSaving('config');
    try {
      await putRegistryRow('config', registryRowId('config', {}), patch as Record<string, unknown>);
      showToast('Ruby Token settings saved', 'success');
      await reloadRegistry();
    } catch (err: any) {
      showToast(err?.message || 'Failed to save Ruby Token settings', 'error');
    } finally {
      setSaving(null);
    }
  };

  if (registryError) {
    return (
      <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-sm">
        <p className="font-semibold">Failed to load the AI registry</p>
        <p className="text-xs mt-0.5">{registryError}</p>
      </div>
    );
  }
  if (!registry) {
    return (
      <RubyLoader variant="page" className="bg-white border border-stone-200 rounded-xl" />
    );
  }

  return (
    <>
      <TokenEconomicsPanel
        config={registry.config}
        saving={saving === 'config'}
        onSave={saveConfig}
        showToast={showToast}
      />
      <CredentialRefsPanel providers={registry.providers} showToast={showToast} />
      <TenantAiSettingsPanel showToast={showToast} />
    </>
  );
};

export default SettingsTab;
